import type { ProductCardDelivery } from './types';

/**
 * A delivery option as the listings API resolves it, which is the input side of
 * this conversion.
 *
 * Easy to confuse with `ProductCardDelivery`, so the difference is the whole
 * reason both exist:
 *
 * | | cost | when |
 * | --- | --- | --- |
 * | `ProductCardDeliveryOption` (this) | **major** units, `3.95` is £3.95 | a lead time in **days** |
 * | `ProductCardDelivery` (the card's props) | **minor** units, `395` is £3.95 | an arrival **date** |
 *
 * Named for the card rather than for the API because it is exported from a design
 * system package, where every other exported type is named for the component it
 * belongs to.
 */
export interface ProductCardDeliveryOption {
  /** Cost in **major** units, as the API sends it: 3.95 means £3.95. */
  cost: number;
  /** Lead time in whole days from `now`. */
  days: number;
}

/**
 * Turns the delivery option the listings API resolved into the card's props.
 *
 * Shared rather than written per surface because of the unit change in the
 * middle of it. The API quotes `cost` in major units and `ProductCardDelivery`
 * takes minor units, and getting that wrong renders £3.95 as 4p or £395. It has
 * already shipped twice as a bug on the PLP grid alone, so a fourth handwritten
 * copy across the recommendations carousels was not worth the risk.
 *
 * The rounding is load-bearing rather than cosmetic: `3.95 * 100` is
 * 395.00000000000006.
 *
 * `now` is a parameter rather than read here so callers can render
 * deterministically in tests and so a page building many cards dates them all
 * from one instant.
 *
 * Deliberately narrow. It converts an option into card props and decides nothing
 * else: whether the product should show a line at all is the caller's, since the
 * rules differ by surface (the PLP grid suppresses it for an unpurchasable
 * product, which shows "Out of stock" in that slot instead).
 */
/* The card accepts `Date | string` so a consumer can pass a serialised date, but
   this always builds a real one, and the narrowed return means a caller can read
   the date back without re-narrowing it. */
export const toProductCardDelivery = (
  { cost, days }: ProductCardDeliveryOption,
  now: Date,
): ProductCardDelivery & { date: Date } => {
  /* Calendar days, not 24-hour periods. `setDate` rolls the month over and keeps
     the local wall-clock time across a DST boundary, where adding days as
     milliseconds would drift the date by an hour and can land it on the wrong
     day. */
  const date = new Date(now.getTime());
  date.setDate(date.getDate() + days);

  return { cost: Math.round(cost * 100), date };
};
